import { Add, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import CustomPagination from '../../components/Pagination';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { deletePrinterModel, listPrinterModels } from '../../grpc/printerModelsApi';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';
import { getPrinterBrandBadgeProps } from '../../helpers/printerBrand';
import { useListFetch } from '../../hooks/useListFetch';

const EMPTY_STATS = {
    total_models: 0,
    total_brands: 0,
};

function ListPrinterModelsPage() {
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checkingAccess, setCheckingAccess] = useState(!isMasterUser());
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(EMPTY_STATS);
    const [brands, setBrands] = useState([]);
    const [draftBrand, setDraftBrand] = useState('');
    const [draftSearch, setDraftSearch] = useState('');
    const [draftFarmLinkSupport, setDraftFarmLinkSupport] = useState('');
    const [draftExperimental, setDraftExperimental] = useState('');
    const [queryFilters, setQueryFilters] = useState({ brand: '', q: '', farm_link_support: '', experimental: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [listVersion, setListVersion] = useState(0);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const hasActiveFilters = Boolean(queryFilters.brand || queryFilters.q || queryFilters.farm_link_support || queryFilters.experimental);

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) {
                        setIsMaster(user.is_master);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsMaster(false);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setCheckingAccess(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const fetchItems = useCallback(() => {
        if (!isMaster) {
            return;
        }
        setIsLoading(true);
        listPrinterModels({
            page: currentPage,
            limit: itemsPerPage,
            q: queryFilters.q || undefined,
            brand: queryFilters.brand || undefined,
            farm_link_support: queryFilters.farm_link_support || undefined,
            experimental: queryFilters.experimental || undefined,
        })
            .then((response) => {
                const data = response?.data || {};
                setItems(data.items || []);
                setTotalPages(data.total_pages || 1);
                setStats(data.stats || EMPTY_STATS);
                setBrands(Array.isArray(data.brands) ? data.brands : []);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [isMaster, currentPage, itemsPerPage, queryFilters.brand, queryFilters.q, queryFilters.farm_link_support, queryFilters.experimental]);

    useListFetch(fetchItems, [
        isMaster,
        currentPage,
        itemsPerPage,
        listVersion,
        queryFilters.brand,
        queryFilters.q,
        queryFilters.farm_link_support,
        queryFilters.experimental,
        fetchItems,
    ]);

    const triggerReload = () => {
        setCurrentPage(1);
        setListVersion((version) => version + 1);
    };

    const handleApplyFilters = () => {
        setQueryFilters({
            brand: draftBrand,
            q: draftSearch.trim(),
            farm_link_support: draftFarmLinkSupport,
            experimental: draftExperimental,
        });
        triggerReload();
    };

    const handleClearFilters = () => {
        setDraftBrand('');
        setDraftSearch('');
        setDraftFarmLinkSupport('');
        setDraftExperimental('');
        setQueryFilters({ brand: '', q: '', farm_link_support: '', experimental: '' });
        triggerReload();
    };

    const handleDelete = (item) => {
        Swal.fire({
            title: 'Remover modelo?',
            text: `${item.brand} ${item.model} será removido do catálogo.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? 'Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
        }).then((result) => {
            if (result.isConfirmed && !loadingDelete) {
                setLoadingDelete(true);
                deletePrinterModel(item.id)
                    .then(() => {
                        setListVersion((version) => version + 1);
                        Swal.fire('Removido!', 'O modelo foi removido com sucesso.', 'success');
                    })
                    .catch(() => {
                        Swal.fire('Erro!', 'Houve um problema ao remover o modelo.', 'error');
                    })
                    .finally(() => {
                        setLoadingDelete(false);
                    });
            }
        });
    };

    if (checkingAccess) {
        return <Loading />;
    }
    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }
    if (isLoading) {
        return <Loading />;
    }
    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Modelos de impressora</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: 2,
            }}>
                <Typography variant="h4" gutterBottom>
                    Modelos de impressora
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/admin/printer-models/new')}
                    startIcon={<Add />}
                    fullWidth={isMobile}
                >
                    Modelo
                </Button>
            </Box>

            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Total de modelos
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700}>
                                {stats.total_models || 0}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Total de marcas
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700}>
                                {stats.total_brands || 0}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Filtros
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-brand-label">Marca</InputLabel>
                            <Select
                                labelId="filter-brand-label"
                                label="Marca"
                                value={draftBrand}
                                onChange={(event) => setDraftBrand(event.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {brands.map((brand) => (
                                    <MenuItem key={brand} value={brand}>
                                        {brand}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Buscar"
                            placeholder="Marca ou modelo"
                            value={draftSearch}
                            onChange={(event) => setDraftSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    handleApplyFilters();
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-farm-link-label">Farm Link</InputLabel>
                            <Select
                                labelId="filter-farm-link-label"
                                label="Farm Link"
                                value={draftFarmLinkSupport}
                                onChange={(event) => setDraftFarmLinkSupport(event.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="implemented">Implementadas</MenuItem>
                                <MenuItem value="not_implemented">Não implementadas</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-experimental-label">Experimental</InputLabel>
                            <Select
                                labelId="filter-experimental-label"
                                label="Experimental"
                                value={draftExperimental}
                                onChange={(event) => setDraftExperimental(event.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="experimental">Sim</MenuItem>
                                <MenuItem value="stable">Não</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Button variant="contained" onClick={handleApplyFilters}>
                        Filtrar
                    </Button>
                    {hasActiveFilters && (
                        <Button variant="outlined" color="secondary" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                            Limpar filtros
                        </Button>
                    )}
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Marca</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Modelo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Consumo médio</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Consumo máximo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Farm Link</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Experimental</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(items) && items.length > 0 ? items.map((item) => {
                            const brandBadge = getPrinterBrandBadgeProps(item.brand);
                            return (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        {brandBadge ? (
                                            <Chip size="small" label={item.brand} {...brandBadge} />
                                        ) : (
                                            item.brand
                                        )}
                                    </TableCell>
                                    <TableCell>{item.model}</TableCell>
                                    <TableCell>{item.avg_consumption_watts} W</TableCell>
                                    <TableCell>{item.max_consumption_watts} W</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            color={item.farm_link_supported ? 'success' : 'default'}
                                            label={item.farm_link_supported ? 'Implementada' : 'Não'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            color={item.experimental ? 'warning' : 'default'}
                                            label={item.experimental ? 'Sim' : 'Não'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="primary"
                                            onClick={() => navigate(`/admin/printer-models/${item.id}/edit`)}
                                        >
                                            <Edit />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(item)}>
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        }) : (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhum modelo cadastrado com os filtros atuais.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    mt: 3,
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        setItemsPerPage(event.target.value);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
        </div>
    );
}

export default ListPrinterModelsPage;
